import { useQueryClient } from "@tanstack/react-query";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { PositionSwapsController } from "../../../api/src/position-swaps/position-swaps.controller";
import type {
  ChangePositionSwapTradeRequest,
  PositionSwap,
  PositionSwapSummary,
} from "../../../api/src/position-swaps/position-swaps.types";
import { useAuthenticatedMutationNew, useAuthenticatedQuery } from "../lib/api";

/** One swap as `GET /firm/position-swaps/:id` serialises it. */
export type PositionSwapData = SerializedObject<PositionSwap>;

/** One swap as `GET /firm/position-swaps` lists it. */
export type PositionSwapSummaryData = SerializedObject<PositionSwapSummary>;

const swapKey = (id: number) => ["firm", "position-swaps", id];

/** The first element of every swap list's key; invalidating it refreshes them all. */
export const POSITION_SWAP_LISTS_KEY = ["firm", "position-swaps", "list"];

/**
 * Every swap of the firm, whatever its status, the most recently updated
 * first, as `GET /firm/position-swaps` answers them. Not read while `enabled`
 * is `false`.
 */
export function useQueryPositionSwaps({ enabled = true } = {}) {
  return useAuthenticatedQuery<
    UnpackResponse<PositionSwapsController["list"]>,
    PositionSwapSummaryData[]
  >({
    queryKey: [...POSITION_SWAP_LISTS_KEY, "all"],
    urlBuilderFn: () => "/firm/position-swaps",
    queryFn: async ({ data }) => data as PositionSwapSummaryData[],
    queryOptions: { enabled },
  });
}

/**
 * The open swaps (draft, approved and executing) out of `symbol`, the most
 * recently updated first, as `GET /firm/position-swaps?symbol=&filter=open`
 * answers them.
 */
export function useQueryOpenPositionSwaps(symbol: string) {
  return useAuthenticatedQuery<
    UnpackResponse<PositionSwapsController["list"]>,
    PositionSwapSummaryData[]
  >({
    queryKey: [...POSITION_SWAP_LISTS_KEY, symbol, "open"],
    urlBuilderFn: () =>
      `/firm/position-swaps?${new URLSearchParams({ symbol, filter: "open" })}`,
    queryFn: async ({ data }) => data as PositionSwapSummaryData[],
  });
}

/** One swap with its accounts, stale accounts and approval blockers. */
export function useQueryPositionSwap(id: number) {
  return useAuthenticatedQuery<
    UnpackResponse<PositionSwapsController["get"]>,
    PositionSwapData
  >({
    queryKey: swapKey(id),
    urlBuilderFn: () => `/firm/position-swaps/${id}`,
    queryFn: async ({ data }) => data as PositionSwapData,
  });
}

/**
 * `<method> /firm/position-swaps/:id/<path>`. Each route answers the swap as
 * it is afterwards, which replaces the cached swap so the screen shows it.
 */
function useSwapAction<TRoute extends keyof PositionSwapsController, TBody>(
  id: number,
  path: string,
  method: "POST" | "PATCH" = "POST",
) {
  const queryClient = useQueryClient();
  return useAuthenticatedMutationNew<
    UnpackResponse<PositionSwapsController[TRoute]>,
    PositionSwapData,
    TBody
  >({
    mutationKey: [method, "firm", "position-swaps", id, path],
    urlBuilderFn: () => `/firm/position-swaps/${id}/${path}`,
    mutationFn: ({ data }) => data as PositionSwapData,
    mutationOptions: {
      onSuccess: (swap) => {
        queryClient.setQueryData(swapKey(id), swap);
        // A cancelled swap leaves the open lists, and a change moves its
        // updated time and editor.
        void queryClient.invalidateQueries({
          queryKey: POSITION_SWAP_LISTS_KEY,
        });
      },
    },
  });
}

type NoBody = Record<string, never>;

/** Approves a draft, with an optional note. */
export const useApprovePositionSwap = (id: number) =>
  useSwapAction<"approve", { note?: string }>(id, "approve");

/** Cancels a draft or an approved swap. */
export const useCancelPositionSwap = (id: number) =>
  useSwapAction<"cancel", NoBody>(id, "cancel");

/** Makes the user the editor of a draft (swap R44). */
export const useTakeOverPositionSwap = (id: number) =>
  useSwapAction<"takeOver", NoBody>(id, "take-over");

/** Plans the stale accounts of a draft again (swap R48). */
export const useRebuildPositionSwap = (id: number) =>
  useSwapAction<"rebuild", NoBody>(id, "rebuild");

/** Cancels the other open proposals of each household of a draft (swap R50). */
export const useCancelOtherProposals = (id: number) =>
  useSwapAction<"cancelOtherProposals", NoBody>(id, "other-proposals/cancel");

/** Cancels the other open proposals of one household of a draft (swap R49). */
export const useCancelHouseholdOtherProposals = (
  id: number,
  householdId: number,
) =>
  useSwapAction<"cancelHouseholdOtherProposals", NoBody>(
    id,
    `households/${householdId}/other-proposals/cancel`,
  );

/** Changes the quantity of one row of a draft, or removes or restores it (swap R45). */
export const useChangePositionSwapTrade = (id: number, tradeId: number) =>
  useSwapAction<"changeTrade", ChangePositionSwapTradeRequest>(
    id,
    `trades/${tradeId}`,
    "PATCH",
  );
